/**
 * Wardogs topçu balistiği — atış çözümü tabloları ve ara değerleme.
 *
 * Tablolar ve ara değerleme mantığı açık kaynak **Wardogs Artillery
 * Calculator**'dan (MIT, https://github.com/apollyon-sys/wardogs-calculator)
 * alınmıştır; ölçümler o projeye aittir, burada TypeScript'e çevrildi.
 *
 * Veri neden kodda: tablolar oyun güncellemesiyle değişir, kullanıcı girdisiyle
 * değil. Bir istek daha açmak yerine bundle'a girerler (iki silah, toplam 223
 * satır) — hesap tamamen tarayıcıda yapılır, backend'e hiç çıkmaz.
 */

/** `[mesafe (m), yükseliş (mil)]` çiftleri. */
type BallisticTable = readonly (readonly [number, number])[]

export interface Weapon {
  id: string
  label: string
  /** Menzil sınırları, metre. Altında ve üstünde atış çözümü yoktur. */
  minRangeM: number
  maxRangeM: number
  /** Namlunun izin verdiği yükseliş aralığı, mil — arayüzde bilgi olarak. */
  minElevationMil: number
  maxElevationMil: number
  /**
   * Yükseliş tabloları. Havan tek çözüm verir (`single`); kundağı motorlu
   * obüsün aynı mesafeye iki yayı vardır — düz (`low`) ve dik (`high`).
   */
  ballistics: {
    single?: BallisticTable
    low?: BallisticTable
    high?: BallisticTable
  }
}

export const WEAPONS: readonly Weapon[] = [
  {
    id: 'mortar',
    label: 'L81 Mortar',
    minRangeM: 132,
    maxRangeM: 684,
    minElevationMil: 150,
    maxElevationMil: 850,
    ballistics: {
      single: [
        [80, 950], [87, 940], [93, 930], [99, 920], [105, 910], [110, 900], [115, 890],
        [118, 880], [122, 870], [127, 860], [132, 850], [140, 840], [151, 830], [163, 820],
        [175, 810], [187, 800], [198, 790], [208, 780], [219, 770], [229, 760], [239, 750],
        [250, 740], [260, 730], [270, 720], [280, 710], [290, 700], [300, 690], [310, 680],
        [319, 670], [329, 660], [339, 650], [348, 640], [358, 630], [367, 620], [376, 610],
        [385, 600], [394, 590], [403, 580], [412, 570], [420, 560], [429, 550], [437, 540],
        [446, 530], [454, 520], [462, 510], [470, 500], [478, 490], [486, 480], [494, 470],
        [501, 460], [509, 450], [516, 440], [524, 430], [531, 420], [538, 410], [545, 400],
        [552, 390], [559, 380], [565, 370], [572, 360], [578, 350], [585, 340], [591, 330],
        [597, 320], [603, 310], [609, 300], [615, 290], [620, 280], [626, 270], [631, 260],
        [636, 250], [641, 240], [646, 230], [651, 220], [656, 210], [661, 200], [666, 190],
        [670, 180], [675, 170], [680, 160], [684, 150], [688, 140], [693, 130], [697, 120],
      ],
    },
  },
  {
    id: 'spg',
    label: 'SPH-2',
    minRangeM: 780,
    maxRangeM: 2629,
    minElevationMil: 20,
    maxElevationMil: 1390,
    ballistics: {
      low: [
        [1181, 20], [1232, 30], [1283, 40], [1334, 50], [1384, 60], [1433, 70], [1482, 80],
        [1529, 90], [1576, 100], [1622, 110], [1666, 120], [1709, 130], [1751, 140],
        [1792, 150], [1832, 160], [1870, 170], [1907, 180], [1944, 190], [1979, 200],
        [2014, 210], [2046, 220], [2079, 230], [2110, 240], [2139, 250], [2168, 260],
        [2196, 270], [2223, 280], [2249, 290], [2273, 300], [2296, 310], [2319, 320],
        [2341, 330], [2362, 340], [2383, 350], [2403, 360], [2422, 370], [2439, 380],
        [2456, 390], [2471, 400], [2485, 410], [2499, 420], [2513, 430], [2526, 440],
        [2538, 450], [2550, 460], [2561, 470], [2570, 480], [2579, 490], [2586, 500],
        [2593, 510], [2599, 520], [2605, 530], [2610, 540], [2615, 550], [2620, 560],
        [2623, 570], [2626, 580], [2628, 590], [2629, 600],
      ],
      high: [
        [2629, 610], [2629, 620], [2628, 630], [2626, 640], [2624, 650], [2621, 660],
        [2617, 670], [2613, 680], [2609, 690], [2604, 700], [2599, 710], [2592, 720],
        [2584, 730], [2576, 740], [2567, 750], [2557, 760], [2546, 770], [2536, 780],
        [2524, 790], [2513, 800], [2501, 810], [2488, 820], [2474, 830], [2460, 840],
        [2444, 850], [2429, 860], [2412, 870], [2395, 880], [2378, 890], [2360, 900],
        [2342, 910], [2323, 920], [2303, 930], [2282, 940], [2261, 950], [2239, 960],
        [2217, 970], [2194, 980], [2171, 990], [2147, 1000], [2123, 1010], [2098, 1020],
        [2072, 1030], [2046, 1040], [2019, 1050], [1991, 1060], [1963, 1070], [1934, 1080],
        [1905, 1090], [1875, 1100], [1844, 1110], [1813, 1120], [1782, 1130], [1750, 1140],
        [1717, 1150], [1684, 1160], [1650, 1170], [1616, 1180], [1582, 1190], [1547, 1200],
        [1512, 1210], [1475, 1220], [1438, 1230], [1401, 1240], [1363, 1250], [1324, 1260],
        [1285, 1270], [1245, 1280], [1205, 1290], [1165, 1300], [1124, 1310], [1083, 1320],
        [1041, 1330], [999, 1340], [956, 1350], [913, 1360], [869, 1370], [825, 1380],
        [780, 1390], [735, 1400],
      ],
    },
  },
]

export const DEFAULT_WEAPON_ID = 'mortar'

export function findWeapon(id: string): Weapon {
  return WEAPONS.find(w => w.id === id) ?? WEAPONS[0]
}

/**
 * Tek bir mesafe için yükseliş çözümü.
 *
 * `mil` tek bir değerdir; tabloda aynı mesafeye birden çok yükseliş yazılıysa
 * (namlu o aralıkta aynı yere düşüyor) `mil` null olur ve aralık `minMil..maxMil`
 * ile verilir — arayüz onu "610–620" diye basar.
 */
export interface MilSolution {
  mil: number | null
  minMil: number
  maxMil: number
}

export interface ElevationSolutions {
  /** Mesafe silahın menzil aralığında mı? */
  inRange: boolean
  single: MilSolution | null
  low: MilSolution | null
  high: MilSolution | null
}

/** Aynı mesafeye yazılmış yükselişleri tek satırda toplar, mesafeye göre sıralar. */
function groupTable(table: BallisticTable): { distance: number; mils: number[] }[] {
  const grouped: { distance: number; mils: number[] }[] = []

  ;[...table]
    .sort((a, b) => a[0] - b[0] || a[1] - b[1])
    .forEach(([distance, mil]) => {
      const previous = grouped[grouped.length - 1]
      if (previous && previous.distance === distance) {
        previous.mils.push(mil)
        return
      }
      grouped.push({ distance, mils: [mil] })
    })

  return grouped
}

function closestMil(values: number[], target: number): number {
  return values.reduce(
    (best, value) => (Math.abs(value - target) < Math.abs(best - target) ? value : best),
    values[0],
  )
}

/**
 * Tablodan mesafeye karşılık gelen yükselişi çözer; iki ölçüm arasındaki
 * mesafede doğrusal ara değerleme yapar.
 *
 * Tablonun dışına **taşmaz**: en yakın ölçüme yapışmak, olmayan bir çözümü
 * varmış gibi göstermek olurdu. Aralığın dışında `null` döner.
 */
function interpolate(table: BallisticTable | undefined, distanceM: number): MilSolution | null {
  if (!table || !table.length || !Number.isFinite(distanceM)) return null

  const groups = groupTable(table)
  const epsilon = 1e-6

  const exact = groups.find(g => Math.abs(g.distance - distanceM) <= epsilon)
  if (exact) {
    const minMil = Math.min(...exact.mils)
    const maxMil = Math.max(...exact.mils)
    return { mil: exact.mils.length === 1 ? exact.mils[0] : null, minMil, maxMil }
  }

  let left: { distance: number; mils: number[] } | null = null
  let right: { distance: number; mils: number[] } | null = null

  for (let i = 0; i < groups.length - 1; i++) {
    if (distanceM > groups[i].distance && distanceM < groups[i + 1].distance) {
      left = groups[i]
      right = groups[i + 1]
      break
    }
  }

  if (!left || !right) return null

  // İki komşu satır birden çok yükseliş taşıyorsa, aralarında geçiş yapılacak
  // çifti seç: birbirine en yakın olanlar aynı yayın üstündedir.
  const rightAverage = right.mils.reduce((sum, v) => sum + v, 0) / right.mils.length
  const leftMil = closestMil(left.mils, rightAverage)
  const rightMil = closestMil(right.mils, leftMil)

  const factor = (distanceM - left.distance) / (right.distance - left.distance)
  const mil = leftMil + factor * (rightMil - leftMil)

  return { mil, minMil: mil, maxMil: mil }
}

/**
 * Verilen mesafe için silahın bütün yükseliş çözümleri.
 *
 * Menzil dışındaysa tablolara hiç bakılmaz: tablo silahın menzilinden biraz
 * taşar (havan tablosu 697 m'ye kadar gider, menzil 684 m'de biter) ve o taşma
 * atılabilir bir atış değildir.
 */
export function elevationSolutions(weapon: Weapon, distanceM: number): ElevationSolutions {
  const empty = { single: null, low: null, high: null }

  if (!Number.isFinite(distanceM)) return { inRange: false, ...empty }

  const inRange =
    distanceM + 1e-6 >= weapon.minRangeM && distanceM <= weapon.maxRangeM + 1e-6

  if (!inRange) return { inRange, ...empty }

  return {
    inRange,
    single: interpolate(weapon.ballistics.single, distanceM),
    low: interpolate(weapon.ballistics.low, distanceM),
    high: interpolate(weapon.ballistics.high, distanceM),
  }
}

/** Çözümü arayüzde basılacak metne çevirir: tek değer ya da "610–620" aralığı. */
export function formatMil(solution: MilSolution | null): string | null {
  if (!solution) return null

  const minMil = Math.round(solution.minMil)
  const maxMil = Math.round(solution.maxMil)

  if (minMil !== maxMil) return `${minMil}–${maxMil}`
  return String(Math.round(solution.mil ?? minMil))
}
